import "./saloonandSpa.css";
import "leaflet/dist/leaflet.css";

import { CircleMarker, MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_CENTER = { lat: 28.6139, lng: 77.2090 };
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const defaultShopTiming = () => ({
  day: "",
  isDayOff: false,
  opening: "10:00",
  closing: "20:00"
});

const normalizeShopTimings = (rows = []) =>
  DAYS.map((day, index) => {
    const current = rows.find((item) => item.day === day) || rows[index] || {};
    return {
      day,
      isDayOff: Boolean(current.isDayOff),
      opening: current.opening || "10:00",
      closing: current.closing || "20:00"
    };
  });

const defaultAddress = () => ({
  line1: "",
  city: "",
  state: "",
  country: "India",
  zip: "",
  lat: "",
  lng: "",
  completeAddress: ""
});

const defaultEmployee = () => ({
  employeeName: "",
  aadhar: "",
  experienceYears: "",
  services: ""
});

const defaultShopEmployee = () => ({
  employeeName: "",
  experienceYears: ""
});

const DEFAULT_GENDERS = ["Male", "Female", "Other"];

const uniqueStrings = (rows = []) =>
  Array.from(new Set(rows.map((row) => String(row || "").trim()).filter(Boolean)));

const ensureOwnerProfile = (value = {}, fallbackCountryCode = "") => ({
  primaryCountryCode: value.primaryCountryCode || fallbackCountryCode,
  primaryPhoneNumber: value.primaryPhoneNumber || "",
  secondaryCountryCode: value.secondaryCountryCode || fallbackCountryCode,
  secondaryPhoneNumber: value.secondaryPhoneNumber || "",
  genderToService: uniqueStrings(value.genderToService),
  servicesOfferedInShop: uniqueStrings(value.servicesOfferedInShop)
});

const ensureHomeProfile = (value = {}, fallbackCountryCode = "") => ({
  primaryCountryCode: value.primaryCountryCode || fallbackCountryCode,
  primaryPhoneNumber: value.primaryPhoneNumber || "",
  secondaryCountryCode: value.secondaryCountryCode || fallbackCountryCode,
  secondaryPhoneNumber: value.secondaryPhoneNumber || "",
  genderToService: uniqueStrings(value.genderToService),
  servicesOfferedAtHome: uniqueStrings(value.servicesOfferedAtHome),
  yearOfExperience: value.yearOfExperience || "",
  availableFrom: value.availableFrom || "10:00",
  availableTill: value.availableTill || "19:00",
  servesNight: Boolean(value.servesNight)
});

const ensureShopConfig = (value = {}, options = {}) => {
  const fallbackCountryCode = options.defaultCountryCode || "";
  const ownerProfile = ensureOwnerProfile(
    value.shopOwnerProfile || {
      genderToService: value.genderToService,
      servicesOfferedInShop: value.selectedSubTypes
    },
    fallbackCountryCode
  );

  const homeProfile = ensureHomeProfile(value.homeServiceProfile || {}, fallbackCountryCode);

  return {
  ownsShop: value.ownsShop || "no",
  shopName: value.shopName || "",
  shopDescription: value.shopDescription || "",
  shopImages: Array.isArray(value.shopImages) ? value.shopImages : [],
  shopVideos: Array.isArray(value.shopVideos) ? value.shopVideos : [],
  shopExperienceProofs: Array.isArray(value.shopExperienceProofs) ? value.shopExperienceProofs : [],
  homeImages: Array.isArray(value.homeImages) ? value.homeImages : [],
  homeVideos: Array.isArray(value.homeVideos) ? value.homeVideos : [],
  homeExperienceProofs: Array.isArray(value.homeExperienceProofs) ? value.homeExperienceProofs : [],
  shopAddress: {
    ...defaultAddress(),
    ...(value.shopAddress || {})
  },
  shopTimings: normalizeShopTimings(value.shopTimings),
  shopOwnerProfile: ownerProfile,
  shopExperienceYears: value.shopExperienceYears || value.yearOfExperience || "",
  listShopEmployees: value.listShopEmployees || "no",
  shopEmployees: Array.isArray(value.shopEmployees) ? value.shopEmployees : [defaultShopEmployee()],
  providesHomeService: value.providesHomeService || value.createHomeServiceProfile || "no",
  createHomeServiceProfile: value.createHomeServiceProfile || value.providesHomeService || "no",
  homeServiceHandledBy: value.homeServiceHandledBy || "owner",
  homeServiceProfile: homeProfile,
  homeServiceEmployees: Array.isArray(value.homeServiceEmployees) ? value.homeServiceEmployees : [defaultEmployee()],
  shopRateBySubtype: value.shopRateBySubtype || {},
  homeRateBySubtype: value.homeRateBySubtype || {},
  averageServiceTimeInMinutes: value.averageServiceTimeInMinutes || "",
  acceptsWalkIns: value.acceptsWalkIns || "yes"
  };
};

const toFileRows = (files) =>
  Array.from(files || []).map((file) => ({
    id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
    file,
    previewUrl: URL.createObjectURL(file)
  }));

const MapInteraction = ({ center, onPick }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);

  useMapEvents({
    click(event) {
      onPick({ lat: event.latlng.lat, lng: event.latlng.lng });
    }
  });

  return null;
};

const SaloonAndSpa = ({
  value,
  onChange,
  serviceOptions = [],
  countryCodeOptions = [],
  defaultCountryCode = ""
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const searchRequestIdRef = useRef(0);

  const config = useMemo(
    () => ensureShopConfig(value, { defaultCountryCode }),
    [defaultCountryCode, value]
  );

  const resolvedCountryOptions = useMemo(() => {
    if (countryCodeOptions.length > 0) {
      return countryCodeOptions;
    }

    return [
      {
        text: defaultCountryCode || "+91",
        value: defaultCountryCode || "+91"
      }
    ];
  }, [countryCodeOptions, defaultCountryCode]);

  const selectedCoords = useMemo(() => {
    const lat = Number(config.shopAddress.lat);
    const lng = Number(config.shopAddress.lng);

    if (Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0) {
      return { lat, lng };
    }

    return DEFAULT_CENTER;
  }, [config.shopAddress.lat, config.shopAddress.lng]);

  const resolvedServiceOptions = useMemo(
    () =>
      (serviceOptions || [])
        .map((row) =>
          typeof row === "string"
            ? { name: row, defaultRate: 0 }
            : {
                name: String(row?.name || "").trim(),
                defaultRate: Number(row?.defaultRate) || 0
              }
        )
        .filter((row) => row.name),
    [serviceOptions]
  );

  const serviceDefaultRateMap = useMemo(
    () =>
      resolvedServiceOptions.reduce((acc, row) => {
        acc[row.name] = row.defaultRate;
        return acc;
      }, {}),
    [resolvedServiceOptions]
  );

  useEffect(() => {
    const normalized = ensureShopConfig(value, { defaultCountryCode });
    const hasDifference = JSON.stringify(normalized) !== JSON.stringify(value || {});
    if (hasDifference) {
      onChange(normalized);
    }
  }, [defaultCountryCode, onChange, value]);

  const updateConfig = (updater) => {
    const nextValue =
      typeof updater === "function"
        ? updater(ensureShopConfig(value, { defaultCountryCode }))
        : updater;
    onChange(nextValue);
  };

  const updateOwnerProfile = (patch) => {
    updateConfig((prev) => ({
      ...prev,
      shopOwnerProfile: {
        ...prev.shopOwnerProfile,
        ...patch
      }
    }));
  };

  const updateHomeProfile = (patch) => {
    updateConfig((prev) => ({
      ...prev,
      homeServiceProfile: {
        ...prev.homeServiceProfile,
        ...patch
      }
    }));
  };

  const toggleOwnerGender = (gender) => {
    updateConfig((prev) => {
      const rows = prev.shopOwnerProfile.genderToService || [];
      const exists = rows.includes(gender);
      return {
        ...prev,
        shopOwnerProfile: {
          ...prev.shopOwnerProfile,
          genderToService: exists ? rows.filter((row) => row !== gender) : [...rows, gender]
        }
      };
    });
  };

  const toggleHomeGender = (gender) => {
    updateConfig((prev) => {
      const rows = prev.homeServiceProfile.genderToService || [];
      const exists = rows.includes(gender);
      return {
        ...prev,
        homeServiceProfile: {
          ...prev.homeServiceProfile,
          genderToService: exists ? rows.filter((row) => row !== gender) : [...rows, gender]
        }
      };
    });
  };

  const toggleOwnerService = (serviceName) => {
    updateConfig((prev) => {
      const rows = prev.shopOwnerProfile.servicesOfferedInShop || [];
      const exists = rows.includes(serviceName);
      const nextRateMap = { ...(prev.shopRateBySubtype || {}) };

      if (exists) {
        delete nextRateMap[serviceName];
      }

      return {
        ...prev,
        shopOwnerProfile: {
          ...prev.shopOwnerProfile,
          servicesOfferedInShop: exists ? rows.filter((row) => row !== serviceName) : [...rows, serviceName]
        },
        shopRateBySubtype: nextRateMap
      };
    });
  };

  const toggleHomeService = (serviceName) => {
    updateConfig((prev) => {
      const rows = prev.homeServiceProfile.servicesOfferedAtHome || [];
      const exists = rows.includes(serviceName);
      const nextRateMap = { ...(prev.homeRateBySubtype || {}) };

      if (exists) {
        delete nextRateMap[serviceName];
      }

      return {
        ...prev,
        homeServiceProfile: {
          ...prev.homeServiceProfile,
          servicesOfferedAtHome: exists ? rows.filter((row) => row !== serviceName) : [...rows, serviceName]
        },
        homeRateBySubtype: nextRateMap
      };
    });
  };

  const updateAddressFieldsFromPayload = (addressPayload = {}, fallback = "") => {
    const city = addressPayload.city || addressPayload.town || addressPayload.village || addressPayload.county || "";
    const state = addressPayload.state || addressPayload.state_district || "";
    const zip = addressPayload.postcode || "";
    const country = addressPayload.country || "India";
    const line1 = [
      addressPayload.house_number,
      addressPayload.road,
      addressPayload.suburb,
      addressPayload.neighbourhood
    ]
      .filter(Boolean)
      .join(", ") || fallback;

    updateConfig((prev) => ({
      ...prev,
      shopAddress: {
        ...prev.shopAddress,
        line1,
        city,
        state,
        country,
        zip,
        completeAddress: [line1, city, state, country].filter(Boolean).join(", ")
      }
    }));
  };

  const setPickedCoordinates = (coords, shouldReverse = false) => {
    updateConfig((prev) => ({
      ...prev,
      shopAddress: {
        ...prev.shopAddress,
        lat: String(coords.lat),
        lng: String(coords.lng)
      }
    }));

    if (!shouldReverse) {
      return;
    }

    fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${encodeURIComponent(coords.lat)}&lon=${encodeURIComponent(coords.lng)}`,
      {
        headers: {
          Accept: "application/json"
        }
      }
    )
      .then((response) => response.json())
      .then((payload) => {
        if (payload?.address) {
          updateAddressFieldsFromPayload(payload.address, payload.display_name || "");
        }
      })
      .catch(() => {});
  };

  const onAssetUpload = (fileList, targetKey) => {
    const rows = toFileRows(fileList);
    if (rows.length === 0) {
      return;
    }

    updateConfig((prev) => ({
      ...prev,
      [targetKey]: [...(prev[targetKey] || []), ...rows]
    }));
  };

  const removeShopAsset = (id, key) => {
    const target = (config[key] || []).find((item) => item.id === id);
    if (target?.previewUrl) {
      URL.revokeObjectURL(target.previewUrl);
    }

    updateConfig((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item.id !== id)
    }));
  };

  const updateTiming = (day, patch) => {
    updateConfig((prev) => ({
      ...prev,
      shopTimings: prev.shopTimings.map((row) =>
        row.day === day
          ? {
              ...row,
              ...patch
            }
          : row
      )
    }));
  };

  const updateEmployee = (index, patch) => {
    updateConfig((prev) => ({
      ...prev,
      homeServiceEmployees: prev.homeServiceEmployees.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              ...patch
            }
          : row
      )
    }));
  };

  const updateShopEmployee = (index, patch) => {
    updateConfig((prev) => ({
      ...prev,
      shopEmployees: prev.shopEmployees.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              ...patch
            }
          : row
      )
    }));
  };

  const addEmployee = () => {
    updateConfig((prev) => ({
      ...prev,
      homeServiceEmployees: [...prev.homeServiceEmployees, defaultEmployee()]
    }));
  };

  const addShopEmployee = () => {
    updateConfig((prev) => ({
      ...prev,
      shopEmployees: [...prev.shopEmployees, defaultShopEmployee()]
    }));
  };

  const removeEmployee = (index) => {
    updateConfig((prev) => {
      if (prev.homeServiceEmployees.length === 1) {
        return {
          ...prev,
          homeServiceEmployees: [defaultEmployee()]
        };
      }

      return {
        ...prev,
        homeServiceEmployees: prev.homeServiceEmployees.filter((_, rowIndex) => rowIndex !== index)
      };
    });
  };

  const removeShopEmployee = (index) => {
    updateConfig((prev) => {
      if (prev.shopEmployees.length === 1) {
        return {
          ...prev,
          shopEmployees: [defaultShopEmployee()]
        };
      }

      return {
        ...prev,
        shopEmployees: prev.shopEmployees.filter((_, rowIndex) => rowIndex !== index)
      };
    });
  };

  const searchPlaces = async (rawQuery, options = {}) => {
    const query = rawQuery.trim();
    const showValidationError = Boolean(options.showValidationError);

    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(showValidationError ? "Enter address or pincode to search." : "");
      return;
    }

    if (query.length < 3) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(showValidationError ? "Type at least 3 characters to search places." : "");
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setSearchLoading(true);
    setSearchError("");

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`,
        {
          headers: {
            Accept: "application/json"
          }
        }
      );

      const payload = await response.json();
      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      if (!Array.isArray(payload) || payload.length === 0) {
        setSearchResults([]);
        setSearchError("No matching address found. Try fuller address or pincode.");
        return;
      }

      setSearchResults(payload);
    } catch {
      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      setSearchError("Address lookup failed. Please try again.");
      setSearchResults([]);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setSearchLoading(false);
      }
    }
  };

  const applySearchResult = (result) => {
    const nextCoords = {
      lat: Number(result.lat),
      lng: Number(result.lon)
    };

    updateAddressFieldsFromPayload(result.address || {}, result.display_name || "");
    setPickedCoordinates(nextCoords, false);
    setSearchQuery(result.display_name || "");
    setSearchResults([]);
    setSearchError("");
  };

  const handleSearchSubmit = async (event) => {
    event.preventDefault();
    await searchPlaces(searchQuery, { showValidationError: true });
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchError("");
      setSearchLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      searchPlaces(searchQuery);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  const renderServiceRateMatrix = ({ title, selectedRows = [], onToggle, rateMapKey }) => {
    if (resolvedServiceOptions.length === 0) {
      return <p className="sas-muted">Service list will appear once subtypes are loaded.</p>;
    }

    return (
      <div className="sas-service-rate-block">
        <h4 className="sas-subtitle">{title}</h4>
        <div className="sas-service-rate-head" aria-hidden="true">
          <span>Service</span>
          <span>Available</span>
          <span>Default Rate</span>
          <span>Your Rate</span>
        </div>

        <div className="sas-service-rate-rows">
          {resolvedServiceOptions.map((option) => {
            const isSelected = selectedRows.includes(option.name);
            return (
              <div className="sas-service-rate-row" key={`${rateMapKey}-${option.name}`}>
                <strong>{option.name}</strong>

                <label className="sas-inline-check sas-service-check">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggle(option.name)}
                  />
                  <span>{isSelected ? "Yes" : "No"}</span>
                </label>

                <p>INR {serviceDefaultRateMap[option.name] || 0}</p>

                <input
                  type="number"
                  min="0"
                  value={config[rateMapKey]?.[option.name] || ""}
                  onChange={(event) =>
                    updateConfig((prev) => ({
                      ...prev,
                      [rateMapKey]: {
                        ...(prev[rateMapKey] || {}),
                        [option.name]: event.target.value
                      }
                    }))
                  }
                  disabled={!isSelected}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="sas-shell">
      <h3 className="sp-section-title">Shop Configuration</h3>

      <div className="sp-grid">
        <label className="sp-field sp-full">
          <span>Shop Configuration *</span>
          <select
            value={config.ownsShop}
            onChange={(event) =>
              updateConfig((prev) => ({
                ...prev,
                ownsShop: event.target.value
              }))
            }
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </label>

        <label className="sp-field sp-full">
          <span>Home Service Configuration</span>
          <select
            value={config.createHomeServiceProfile}
            onChange={(event) =>
              updateConfig((prev) => ({
                ...prev,
                createHomeServiceProfile: event.target.value,
                providesHomeService: event.target.value
              }))
            }
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </label>
      </div>

      <div className="sas-config-columns">

      {config.ownsShop === "yes" && (
        <section className="sas-config-column">
          <section className="sas-panel">
            <h4 className="sas-panel-title">Shop Profile</h4>
            <div className="sp-grid">
            <label className="sp-field">
              <span>Shop Owner Primary Country Code</span>
              <select
                value={config.shopOwnerProfile.primaryCountryCode}
                onChange={(event) =>
                  updateOwnerProfile({
                    primaryCountryCode: event.target.value
                  })
                }
              >
                {resolvedCountryOptions.map((option) => (
                  <option key={`shop-primary-${option.value}`} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </label>

            <label className="sp-field">
              <span>Shop Owner Primary Phone Number</span>
              <input
                type="text"
                value={config.shopOwnerProfile.primaryPhoneNumber}
                onChange={(event) =>
                  updateOwnerProfile({
                    primaryPhoneNumber: event.target.value
                  })
                }
              />
            </label>

            <label className="sp-field">
              <span>Shop Owner Secondary Country Code</span>
              <select
                value={config.shopOwnerProfile.secondaryCountryCode}
                onChange={(event) =>
                  updateOwnerProfile({
                    secondaryCountryCode: event.target.value
                  })
                }
              >
                {resolvedCountryOptions.map((option) => (
                  <option key={`shop-secondary-${option.value}`} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </label>

            <label className="sp-field">
              <span>Shop Owner Secondary Phone Number</span>
              <input
                type="text"
                value={config.shopOwnerProfile.secondaryPhoneNumber}
                onChange={(event) =>
                  updateOwnerProfile({
                    secondaryPhoneNumber: event.target.value
                  })
                }
              />
            </label>

            <div className="sp-field sp-full">
              <span>Gender To Service (Shop)</span>
              <div className="sp-multi-options">
                {DEFAULT_GENDERS.map((gender) => (
                  <label key={`shop-gender-${gender}`}>
                    <input
                      type="checkbox"
                      checked={config.shopOwnerProfile.genderToService.includes(gender)}
                      onChange={() => toggleOwnerGender(gender)}
                    />
                    <span>{gender}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="sp-field sp-full">
              <span>Shop Services + Rates</span>
              {renderServiceRateMatrix({
                title: "Service Availability and Pricing (Shop)",
                selectedRows: config.shopOwnerProfile.servicesOfferedInShop,
                onToggle: toggleOwnerService,
                rateMapKey: "shopRateBySubtype"
              })}
              <small className="sas-muted">You can keep more services in shop than home profile.</small>
            </div>

            <label className="sp-field">
              <span>Shop Experience (years)</span>
              <input
                type="number"
                min="0"
                value={config.shopExperienceYears}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopExperienceYears: event.target.value
                  }))
                }
              />
            </label>

            <label className="sp-field">
              <span>Shop Name *</span>
              <input
                type="text"
                value={config.shopName}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopName: event.target.value
                  }))
                }
              />
            </label>

            <label className="sp-field">
              <span>Average Service Time (minutes)</span>
              <input
                type="number"
                min="0"
                value={config.averageServiceTimeInMinutes}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    averageServiceTimeInMinutes: event.target.value
                  }))
                }
              />
            </label>

            <label className="sp-field sp-full">
              <span>Shop Description</span>
              <textarea
                rows="3"
                value={config.shopDescription}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopDescription: event.target.value
                  }))
                }
              />
            </label>

            <label className="sp-field">
              <span>Accept walk-in customers?</span>
              <select
                value={config.acceptsWalkIns}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    acceptsWalkIns: event.target.value
                  }))
                }
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            </div>

            <div className="sp-grid">
              <label className="sp-field sp-full">
                <span>Do you want to list down your shop employees?</span>
                <select
                  value={config.listShopEmployees}
                  onChange={(event) =>
                    updateConfig((prev) => ({
                      ...prev,
                      listShopEmployees: event.target.value
                    }))
                  }
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
            </div>

            {config.listShopEmployees === "yes" && (
              <>
                <h4 className="sas-subtitle">Shop Employees</h4>
                <div className="sas-employee-list">
                  {config.shopEmployees.map((employee, index) => (
                    <div className="sas-employee-card" key={`shop-employee-${index}`}>
                      <div className="sas-employee-head">
                        <strong>Shop Employee {index + 1}</strong>
                        <button type="button" onClick={() => removeShopEmployee(index)}>
                          Remove
                        </button>
                      </div>

                      <div className="sp-grid">
                        <label className="sp-field">
                          <span>Employee Name</span>
                          <input
                            type="text"
                            value={employee.employeeName || ""}
                            onChange={(event) =>
                              updateShopEmployee(index, {
                                employeeName: event.target.value
                              })
                            }
                          />
                        </label>

                        <label className="sp-field">
                          <span>Experience (years)</span>
                          <input
                            type="number"
                            min="0"
                            value={employee.experienceYears || ""}
                            onChange={(event) =>
                              updateShopEmployee(index, {
                                experienceYears: event.target.value
                              })
                            }
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <button type="button" className="sp-reset" onClick={addShopEmployee}>
                  + Add Shop Employee
                </button>
              </>
            )}

            <h4 className="sas-subtitle">Media Upload For Shop</h4>

            <h4 className="sas-subtitle">1. Courses &amp; Ceriticates - Show people your experince which can justify your rates</h4>
            <label className="sp-upload-label">
              <input
                type="file"
                accept="application/pdf,image/*"
                multiple
                onChange={(event) => {
                  onAssetUpload(event.target.files, "shopExperienceProofs");
                  event.target.value = "";
                }}
              />
              <span>Upload your shop courses and ceriticates</span>
            </label>
            <div className="sp-upload-list">
              {config.shopExperienceProofs.map((item) => (
                <article key={item.id}>
                  {item.file?.type?.startsWith("image/") ? (
                    <img src={item.previewUrl} alt={item.file?.name || "Shop certificate"} />
                  ) : (
                    <div className="sp-file-placeholder">FILE</div>
                  )}
                  <div>
                    <strong>{item.file?.name || "Document"}</strong>
                    <small>{item.file?.size ? `${Math.round(item.file.size / 1024)} KB` : ""}</small>
                  </div>
                  <button type="button" onClick={() => removeShopAsset(item.id, "shopExperienceProofs")}>Delete</button>
                </article>
              ))}
            </div>

            <h4 className="sas-subtitle">2. Showcase your work on site</h4>
            <label className="sp-upload-label">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => {
                  onAssetUpload(event.target.files, "shopImages");
                  event.target.value = "";
                }}
              />
              <span>Upload images of work done in shop</span>
            </label>
            <div className="sp-upload-list">
              {config.shopImages.map((item) => (
                <article key={item.id}>
                  {item.file?.type?.startsWith("image/") ? (
                    <img src={item.previewUrl} alt={item.file?.name || "Shop work image"} />
                  ) : (
                    <div className="sp-file-placeholder">IMAGE</div>
                  )}
                  <div>
                    <strong>{item.file?.name || "Image"}</strong>
                    <small>{item.file?.size ? `${Math.round(item.file.size / 1024)} KB` : ""}</small>
                  </div>
                  <button type="button" onClick={() => removeShopAsset(item.id, "shopImages")}>Delete</button>
                </article>
              ))}
            </div>

            <label className="sp-upload-label">
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={(event) => {
                  onAssetUpload(event.target.files, "shopVideos");
                  event.target.value = "";
                }}
              />
              <span>Upload videos of work done in shop</span>
            </label>
            <div className="sp-upload-list">
              {config.shopVideos.map((item) => (
                <article key={item.id}>
                  <div className="sp-file-placeholder">VIDEO</div>
                  <div>
                    <strong>{item.file?.name || "Video"}</strong>
                    <small>{item.file?.size ? `${Math.round(item.file.size / 1024)} KB` : ""}</small>
                  </div>
                  <button type="button" onClick={() => removeShopAsset(item.id, "shopVideos")}>Delete</button>
                </article>
              ))}
            </div>
          </section>

          <h4 className="sas-subtitle">Shop Address (one address only)</h4>
          <form className="sas-search-row" onSubmit={handleSearchSubmit}>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search area, landmark, pincode"
            />
            <button type="submit" className="sp-submit" disabled={searchLoading}>
              {searchLoading ? "Searching..." : "Search"}
            </button>
          </form>

          {searchError && <p className="sp-error">{searchError}</p>}

          {searchResults.length > 0 && (
            <div className="sas-search-results">
              {searchResults.map((result) => (
                <button type="button" key={result.place_id} onClick={() => applySearchResult(result)}>
                  <strong>{result.display_name}</strong>
                </button>
              ))}
            </div>
          )}

          <div className="sas-map-wrap">
            <MapContainer center={selectedCoords} zoom={14} scrollWheelZoom className="sas-leaflet-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapInteraction
                center={selectedCoords}
                onPick={(coords) => {
                  setPickedCoordinates(coords, true);
                }}
              />
              <CircleMarker center={selectedCoords} radius={9} pathOptions={{ color: "#0f766e", fillColor: "#f97316", fillOpacity: 0.9 }} />
            </MapContainer>
            <small>Click on map to pin your exact shop location.</small>
          </div>

          <div className="sp-grid">
            <label className="sp-field sp-full">
              <span>Address Line *</span>
              <input
                type="text"
                value={config.shopAddress.line1}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopAddress: {
                      ...prev.shopAddress,
                      line1: event.target.value
                    }
                  }))
                }
              />
            </label>
            <label className="sp-field">
              <span>City</span>
              <input
                type="text"
                value={config.shopAddress.city}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopAddress: {
                      ...prev.shopAddress,
                      city: event.target.value
                    }
                  }))
                }
              />
            </label>
            <label className="sp-field">
              <span>State</span>
              <input
                type="text"
                value={config.shopAddress.state}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopAddress: {
                      ...prev.shopAddress,
                      state: event.target.value
                    }
                  }))
                }
              />
            </label>
            <label className="sp-field">
              <span>Country</span>
              <input
                type="text"
                value={config.shopAddress.country}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopAddress: {
                      ...prev.shopAddress,
                      country: event.target.value
                    }
                  }))
                }
              />
            </label>
            <label className="sp-field">
              <span>Zip</span>
              <input
                type="text"
                value={config.shopAddress.zip}
                onChange={(event) =>
                  updateConfig((prev) => ({
                    ...prev,
                    shopAddress: {
                      ...prev.shopAddress,
                      zip: event.target.value
                    }
                  }))
                }
              />
            </label>
          </div>

          <h4 className="sas-subtitle">Shop Timings</h4>
          <div className="sas-timings-table-wrap">
            <table className="sas-timings-table" aria-label="Shop timings">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Day Off</th>
                  <th>Opening</th>
                  <th>Closing</th>
                </tr>
              </thead>
              <tbody>
                {config.shopTimings.map((row) => (
                  <tr key={row.day}>
                    <td>{row.day}</td>
                    <td>
                      <label className="sas-inline-check">
                        <input
                          type="checkbox"
                          checked={row.isDayOff}
                          onChange={(event) =>
                            updateTiming(row.day, {
                              isDayOff: event.target.checked
                            })
                          }
                        />
                        <span>{row.isDayOff ? "Weekend Off" : "Working"}</span>
                      </label>
                    </td>
                    <td>
                      <input
                        type="time"
                        value={row.opening}
                        disabled={row.isDayOff}
                        onChange={(event) =>
                          updateTiming(row.day, {
                            opening: event.target.value
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={row.closing}
                        disabled={row.isDayOff}
                        onChange={(event) =>
                          updateTiming(row.day, {
                            closing: event.target.value
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {config.ownsShop !== "yes" && (
        <section className="sas-config-column sas-config-placeholder">
          <h4 className="sas-panel-title">Shop Configuration</h4>
          <p className="sas-muted">Enable shop configuration to set shop profile, address, timings, and media.</p>
        </section>
      )}

      {config.createHomeServiceProfile === "yes" && (
        <section className="sas-config-column">
          <section className="sas-panel">
                <h4 className="sas-panel-title">Home Profile</h4>
                <div className="sp-grid">
                <label className="sp-field">
                  <span>Home Service Primary Country Code</span>
                  <select
                    value={config.homeServiceProfile.primaryCountryCode}
                    onChange={(event) =>
                      updateHomeProfile({
                        primaryCountryCode: event.target.value
                      })
                    }
                  >
                    {resolvedCountryOptions.map((option) => (
                      <option key={`home-primary-${option.value}`} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="sp-field">
                  <span>Home Service Primary Phone Number</span>
                  <input
                    type="text"
                    value={config.homeServiceProfile.primaryPhoneNumber}
                    onChange={(event) =>
                      updateHomeProfile({
                        primaryPhoneNumber: event.target.value
                      })
                    }
                  />
                </label>

                <label className="sp-field">
                  <span>Home Service Secondary Country Code</span>
                  <select
                    value={config.homeServiceProfile.secondaryCountryCode}
                    onChange={(event) =>
                      updateHomeProfile({
                        secondaryCountryCode: event.target.value
                      })
                    }
                  >
                    {resolvedCountryOptions.map((option) => (
                      <option key={`home-secondary-${option.value}`} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="sp-field">
                  <span>Home Service Secondary Phone Number</span>
                  <input
                    type="text"
                    value={config.homeServiceProfile.secondaryPhoneNumber}
                    onChange={(event) =>
                      updateHomeProfile({
                        secondaryPhoneNumber: event.target.value
                      })
                    }
                  />
                </label>

                <div className="sp-field sp-full">
                  <span>Gender To Service (Home)</span>
                  <div className="sp-multi-options">
                    {DEFAULT_GENDERS.map((gender) => (
                      <label key={`home-gender-${gender}`}>
                        <input
                          type="checkbox"
                          checked={config.homeServiceProfile.genderToService.includes(gender)}
                          onChange={() => toggleHomeGender(gender)}
                        />
                        <span>{gender}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="sp-field sp-full">
                  <span>Home Services + Rates</span>
                  {renderServiceRateMatrix({
                    title: "Service Availability and Pricing (Home)",
                    selectedRows: config.homeServiceProfile.servicesOfferedAtHome,
                    onToggle: toggleHomeService,
                    rateMapKey: "homeRateBySubtype"
                  })}
                </div>

                <label className="sp-field">
                  <span>Home Service Experience (years)</span>
                  <input
                    type="number"
                    min="0"
                    value={config.homeServiceProfile.yearOfExperience}
                    onChange={(event) =>
                      updateHomeProfile({
                        yearOfExperience: event.target.value
                      })
                    }
                  />
                </label>

                <label className="sp-field">
                  <span>Available From</span>
                  <input
                    type="time"
                    value={config.homeServiceProfile.availableFrom}
                    onChange={(event) =>
                      updateHomeProfile({
                        availableFrom: event.target.value
                      })
                    }
                  />
                </label>

                <label className="sp-field">
                  <span>Available Till</span>
                  <input
                    type="time"
                    value={config.homeServiceProfile.availableTill}
                    onChange={(event) =>
                      updateHomeProfile({
                        availableTill: event.target.value
                      })
                    }
                  />
                </label>

                <label className="sp-field sp-checkbox">
                  <input
                    type="checkbox"
                    checked={config.homeServiceProfile.servesNight}
                    onChange={(event) =>
                      updateHomeProfile({
                        servesNight: event.target.checked
                      })
                    }
                  />
                  <span>Also serves at night</span>
                </label>
                </div>

                <div className="sp-grid">
                  <label className="sp-field sp-full">
                    <span>For home service, who will provide service?</span>
                    <select
                      value={config.homeServiceHandledBy}
                      onChange={(event) =>
                        updateConfig((prev) => ({
                          ...prev,
                          homeServiceHandledBy: event.target.value
                        }))
                      }
                    >
                      <option value="owner">Owner will provide</option>
                      <option value="employees">Only employees will provide</option>
                      <option value="both">Owner and employees both will provide</option>
                    </select>
                  </label>
                </div>

                {config.homeServiceHandledBy !== "owner" && (
                  <>
                    <h4 className="sas-subtitle">Home Service Employees</h4>
                    <div className="sas-employee-list">
                      {config.homeServiceEmployees.map((employee, index) => (
                        <div className="sas-employee-card" key={`employee-${index}`}>
                          <div className="sas-employee-head">
                            <strong>Employee {index + 1}</strong>
                            <button type="button" onClick={() => removeEmployee(index)}>
                              Remove
                            </button>
                          </div>

                          <div className="sp-grid">
                            <label className="sp-field">
                              <span>Employee Name</span>
                              <input
                                type="text"
                                value={employee.employeeName}
                                onChange={(event) =>
                                  updateEmployee(index, {
                                    employeeName: event.target.value
                                  })
                                }
                              />
                            </label>

                            <label className="sp-field">
                              <span>Aadhar (mandatory)</span>
                              <input
                                type="text"
                                value={employee.aadhar}
                                onChange={(event) =>
                                  updateEmployee(index, {
                                    aadhar: event.target.value
                                  })
                                }
                              />
                            </label>

                            <label className="sp-field">
                              <span>Experience (years)</span>
                              <input
                                type="number"
                                min="0"
                                value={employee.experienceYears || ""}
                                onChange={(event) =>
                                  updateEmployee(index, {
                                    experienceYears: event.target.value
                                  })
                                }
                              />
                            </label>

                            <label className="sp-field sp-full">
                              <span>Services Provided by this Employee</span>
                              <input
                                type="text"
                                value={employee.services}
                                onChange={(event) =>
                                  updateEmployee(index, {
                                    services: event.target.value
                                  })
                                }
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button type="button" className="sp-reset" onClick={addEmployee}>
                      + Add Employee
                    </button>
                  </>
                )}

                <h4 className="sas-subtitle">Media Upload For Home Service</h4>

                <h4 className="sas-subtitle">Work Done Images/Videos</h4>
                <label className="sp-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) => {
                      onAssetUpload(event.target.files, "homeImages");
                      event.target.value = "";
                    }}
                  />
                  <span>Upload home service work images</span>
                </label>
                <div className="sp-upload-list">
                  {config.homeImages.map((item) => (
                    <article key={item.id}>
                      {item.file?.type?.startsWith("image/") ? (
                        <img src={item.previewUrl} alt={item.file?.name || "Home work image"} />
                      ) : (
                        <div className="sp-file-placeholder">IMAGE</div>
                      )}
                      <div>
                        <strong>{item.file?.name || "Image"}</strong>
                        <small>{item.file?.size ? `${Math.round(item.file.size / 1024)} KB` : ""}</small>
                      </div>
                      <button type="button" onClick={() => removeShopAsset(item.id, "homeImages")}>Delete</button>
                    </article>
                  ))}
                </div>

                <label className="sp-upload-label">
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={(event) => {
                      onAssetUpload(event.target.files, "homeVideos");
                      event.target.value = "";
                    }}
                  />
                  <span>Upload home service work videos</span>
                </label>
                <div className="sp-upload-list">
                  {config.homeVideos.map((item) => (
                    <article key={item.id}>
                      <div className="sp-file-placeholder">VIDEO</div>
                      <div>
                        <strong>{item.file?.name || "Video"}</strong>
                        <small>{item.file?.size ? `${Math.round(item.file.size / 1024)} KB` : ""}</small>
                      </div>
                      <button type="button" onClick={() => removeShopAsset(item.id, "homeVideos")}>Delete</button>
                    </article>
                  ))}
                </div>

                <h4 className="sas-subtitle">Your experince certificates or courses completed cetifiticates</h4>
                <label className="sp-upload-label">
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    multiple
                    onChange={(event) => {
                      onAssetUpload(event.target.files, "homeExperienceProofs");
                      event.target.value = "";
                    }}
                  />
                  <span>Upload home service certificates and course proof</span>
                </label>
                <div className="sp-upload-list">
                  {config.homeExperienceProofs.map((item) => (
                    <article key={item.id}>
                      {item.file?.type?.startsWith("image/") ? (
                        <img src={item.previewUrl} alt={item.file?.name || "Home service proof"} />
                      ) : (
                        <div className="sp-file-placeholder">FILE</div>
                      )}
                      <div>
                        <strong>{item.file?.name || "Document"}</strong>
                        <small>{item.file?.size ? `${Math.round(item.file.size / 1024)} KB` : ""}</small>
                      </div>
                      <button type="button" onClick={() => removeShopAsset(item.id, "homeExperienceProofs")}>Delete</button>
                    </article>
                  ))}
                </div>
          </section>

        </section>
      )}

      {config.createHomeServiceProfile !== "yes" && (
        <section className="sas-config-column sas-config-placeholder">
          <h4 className="sas-panel-title">Home Service Configuration</h4>
          <p className="sas-muted">Enable home service configuration to assign home availability and staffing.</p>
        </section>
      )}

      </div>
    </div>
  );
};

export default SaloonAndSpa;
